import { useState, useEffect } from "react";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
} from "@mui/x-data-grid";
import { Button, Box } from "@mui/material";
import { useTheme } from "@emotion/react";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";

// Components
import { useNotification } from "../utils/Notification";
import { trackEvent } from "../utils/Utils";
import Template from "../modals/Template";
import Plans from "../modals/Plans";
import Header from "./Header";

// Theme
import { tokens } from "../../theme";

// Supabase
import supabase from "../../supabaseClient";

export default function Templates() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [templates, setTemplates] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [plansModalOpen, setPlansModalOpen] = useState(false);
  const [planData, setPlanData] = useState([]);
  const { t } = useTranslation();

  // Context
  const { showNotification } = useNotification();

  const columns = [
    { field: "id", headerName: t("templates.columns.id"), width: 230 },
    { field: "name", headerName: t("templates.columns.name"), width: 200 },
    {
      field: "content",
      headerName: t("templates.columns.content"),
      width: 400,
    },
    { field: "file_name", headerName: t("templates.columns.file"), width: 200 },
  ];

  useEffect(() => {
    const fetchTemplates = async () => {
      const { data, error } = await supabase.from("templates").select(
        `
            id,
            name,
            content,
            file_id(id, name)
          `
      );

      if (error) {
        showNotification(t("templates.fetchError", { error: error.message }));
      } else {
        const transformedData = data.map((template) => ({
          ...template,
          file_name: template.file_id?.name || "",
        }));
        setTemplates(transformedData);
      }
    };

    const fetchPlan = async () => {
      const { data, error } = await supabase
        .from("teams")
        .select("plan(templates_limit)")
        .single();

      if (!error) {
        setPlanData(data.plan);
      }
    };

    fetchTemplates();
    fetchPlan();
  }, [modalOpen, plansModalOpen]);

  const handleNewTemplate = () => {
    if (planData.templates_limit <= templates.length) {
      showNotification(t("templates.templatesLimitError"), "warning");
      setPlansModalOpen(true);
      return;
    }
    trackEvent("templates-new_template_clicked");
    setIsEditing(false);
    setSelectedTemplate(null);
    setModalOpen(true);
  };

  const handleRowClick = (params) => {
    trackEvent("templates-template_row_clicked");
    setIsEditing(true);
    setSelectedTemplate(params.row);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    trackEvent("templates-template_modal_closed");
    setModalOpen(false);
    setIsEditing(false);
    setSelectedTemplate(null);
  };

  const handleSubmit = () => {
    trackEvent("templates-template_modal_submitted");
    setModalOpen(false);
  };

  const handleClosePlansModal = () => {
    trackEvent("templates-plan_modal_closed");
    setPlansModalOpen(false);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: "1600px", mx: "auto" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Header
          title={t("templates.title")}
          subtitle={t("templates.subtitle")}
        />
      </Box>

      {/* Content wrapper with glassmorphism */}
      <Box
        sx={{
          bgcolor: theme.palette.mode === 'dark' ? "rgba(20, 27, 45, 0.7)" : "#ffffff",
          backdropFilter: "blur(10px)",
          borderRadius: "16px",
          boxShadow: theme.palette.mode === 'dark' ? "0 8px 32px 0 rgba(0, 0, 0, 0.3)" : "0 8px 32px 0 rgba(31, 38, 135, 0.05)",
          border: theme.palette.mode === 'dark' ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid rgba(0, 0, 0, 0.05)",
          p: { xs: 2, md: 3 },
          minHeight: "500px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            height: "auto",
            minHeight: "400px",
            width: "100%",
            "& .MuiDataGrid-root": {
              border: "none",
              bgcolor: "transparent",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: theme.palette.mode === 'dark' ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid rgba(0, 0, 0, 0.05)",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.mode === 'dark' ? "rgba(0,0,0,0.2)" : "#f8f9fa",
              borderBottom: "none",
              color: theme.palette.mode === 'dark' ? "#a1a4ab" : "#141b2d",
              fontWeight: "bold",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: "transparent",
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: theme.palette.mode === 'dark' ? "rgba(0,0,0,0.2)" : "#f8f9fa",
              borderBottomLeftRadius: "16px",
              borderBottomRightRadius: "16px",
            },
            "& .MuiCheckbox-root": {
              color: `${theme.palette.mode === 'dark' ? "#4cceac" : "#3da58a"} !important`,
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${theme.palette.mode === 'dark' ? "#a1a4ab" : "#727681"} !important`,
              fontWeight: "bold",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.02)",
            },
          }}
        >
          <DataGrid
            rows={templates}
            columns={columns}
            onRowClick={handleRowClick}
            disableSelectionOnClick
            autoHeight
            sx={{ borderRadius: "16px", overflow: "hidden" }}
            components={{
              Toolbar: () => (
                <GridToolbarContainer sx={{ p: 2, borderBottom: theme.palette.mode === 'dark' ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.05)", gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleNewTemplate}
                    sx={{
                      borderRadius: "8px",
                      fontWeight: "bold",
                      background: "linear-gradient(90deg, #7949E7 0%, #9A67ED 100%)",
                      color: "#fff",
                      boxShadow: "0 4px 14px 0 rgba(121, 73, 231, 0.39)",
                      "&:hover": { background: "linear-gradient(90deg, #6B53BB 0%, #7949E7 100%)" }
                    }}
                  >
                    {t("templates.newTemplate")}
                  </Button>

                  <Box sx={{ flexGrow: 1 }} />

                  <GridToolbarFilterButton />
                  <GridToolbarColumnsButton />
                  <GridToolbarDensitySelector />
                </GridToolbarContainer>
              ),
            }}
          />
        </Box>
      </Box>

      <Template
        open={modalOpen}
        onClose={handleCloseModal}
        isEditing={isEditing}
        template={selectedTemplate}
        onSubmit={handleSubmit}
      />

      <Plans open={plansModalOpen} onClose={handleClosePlansModal} />
    </Box>
  );
}
